import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Clock, Car, Footprints, Phone, ExternalLink, Bell, Bus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Mosque {
  id: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  distance: number;
  walking_time: number;
  driving_time: number;
  transit_time: number;
  jummah_time?: string;
  jummah_khutbah_time?: string;
  phone?: string;
}

interface NearbyMosquesProps {
  timeUntilJummah: number | null; // hours or null if not applicable
  onMosqueSelected?: (mosque: Mosque | null) => void;
}

const NearbyMosques = ({ timeUntilJummah, onMosqueSelected }: NearbyMosquesProps) => {
  const [mosques, setMosques] = useState<Mosque[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMosqueId, setSelectedMosqueId] = useState<string | null>(null);

  const handleMosqueSelect = (mosque: Mosque) => {
    const isSelected = selectedMosqueId === mosque.id;
    const newSelectedId = isSelected ? null : mosque.id;
    setSelectedMosqueId(newSelectedId);
    onMosqueSelected?.(isSelected ? null : mosque);
  };

  const fetchNearbyMosques = async () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by this browser");
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { data, error } = await supabase.functions.invoke('find-nearby-mosques', {
            body: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              limit: 2
            }
          });

          if (error) throw error;
          
          setMosques(data.mosques || []);
          setError(null);
        } catch (err: any) {
          console.error('Error fetching nearby mosques:', err);
          setError('Failed to find nearby mosques');
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        console.error("Error getting location:", error);
        setError("Unable to get your location");
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    fetchNearbyMosques();
  }, []);

  // Restore previously selected mosque from localStorage when list loads
  useEffect(() => {
    try {
      const saved = localStorage.getItem('jummah-selected-mosque');
      if (!saved) return;
      const savedMosque = JSON.parse(saved) as Mosque;
      const match = mosques.find((m) => m.id === savedMosque.id);
      if (match) {
        setSelectedMosqueId(match.id);
        onMosqueSelected?.(match);
      } else {
        setSelectedMosqueId(null);
      }
    } catch {
      // ignore
    }
  }, [mosques]);

  const getRecommendation = (mosque: Mosque) => {
    const now = new Date();
    const isFriday = now.getDay() === 5; // 5 = Friday
    
    // Only show time recommendations on Friday before Jummah and when timeUntilJummah is provided
    if (!isFriday || timeUntilJummah === null || timeUntilJummah <= 0) {
      return null; // Don't show any recommendation
    }
    
    const walkingLeaveTime = timeUntilJummah - (mosque.walking_time / 60);
    const drivingLeaveTime = timeUntilJummah - (mosque.driving_time / 60);
    const transitLeaveTime = timeUntilJummah - (mosque.transit_time / 60);

    // If Jummah has already passed today, don't show recommendation
    if (walkingLeaveTime < 0) {
      return null;
    }

    // Helper function to format time in a readable way
    const formatTime = (hours: number) => {
      if (hours < 1) {
        return `${Math.round(hours * 60)} min`;
      } else {
        const wholeHours = Math.floor(hours);
        const minutes = Math.round((hours - wholeHours) * 60);
        if (minutes === 0) {
          return `${wholeHours} ${wholeHours === 1 ? 'hour' : 'hours'}`;
        }
        return `${wholeHours}h ${minutes}m`;
      }
    };

    if (walkingLeaveTime <= 0.08) { // Less than 5 minutes
      // Choose fastest option between driving and transit
      const fastestTime = Math.min(mosque.driving_time, mosque.transit_time);
      const fastestMode = mosque.driving_time <= mosque.transit_time ? "car" : "transit";
      return {
        transport: fastestMode === "car" ? "driving" : "transit",
        urgency: "urgent",
        message: `Leave NOW by ${fastestMode} (${fastestTime} min)`
      };
    } else if (walkingLeaveTime <= 0.5) { // Less than 30 minutes
      // Recommend best option between driving and transit
      const bestLeaveTime = Math.max(drivingLeaveTime, transitLeaveTime);
      const bestMode = drivingLeaveTime >= transitLeaveTime ? "car" : "transit";
      return {
        transport: bestMode === "car" ? "driving" : "transit",
        urgency: "soon",
        message: `Leave in ${formatTime(bestLeaveTime)} by ${bestMode}`
      };
    } else if (walkingLeaveTime <= 1) { // Less than 1 hour
      // Check if transit is reasonable compared to walking
      if (mosque.transit_time < mosque.walking_time * 0.7) {
        return {
          transport: "transit",
          urgency: "normal", 
          message: `Leave in ${formatTime(transitLeaveTime)} by transit`
        };
      } else {
        return {
          transport: "walking",
          urgency: "normal", 
          message: `Leave in ${formatTime(walkingLeaveTime)} walking`
        };
      }
    } else {
      return {
        transport: "walking",
        urgency: "plenty",
        message: `Plenty of time - ${formatTime(walkingLeaveTime)} to walk`
      };
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-primary flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Nearby Mosques for Jummah
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="animate-pulse">Finding nearby mosques...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-primary flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Nearby Mosques for Jummah
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-destructive">
            {error}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (mosques.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-primary flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Nearby Mosques for Jummah
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p>No mosques found nearby. Try expanding your search radius.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-primary flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Nearby Mosques for Jummah ({mosques.length} found)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {mosques.map((mosque, index) => {
            const recommendation = getRecommendation(mosque);
            
            return (
              <div key={mosque.id} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg">{mosque.name}</h3>
                      {index === 0 && (
                        <Badge variant="secondary" className="bg-accent/20 text-accent">
                          Closest
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                      <MapPin className="h-4 w-4" />
                      {mosque.address}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                      <Clock className="h-4 w-4" />
                      Jummah: {mosque.jummah_time || 'Time not available'}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {mosque.distance} km away
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                  <div className="flex items-center justify-start gap-1">
                    <Footprints className="h-4 w-4" />
                    <span>{mosque.walking_time} min walk</span>
                  </div>
                  <div className="flex items-center justify-center gap-1">
                    <Car className="h-4 w-4" />
                    <span>{mosque.driving_time} min drive</span>
                  </div>
                  <div className="flex items-center justify-end gap-1">
                    <Bus className="h-4 w-4" />
                    <span>{mosque.transit_time} min transit</span>
                  </div>
                </div>

                {recommendation && (
                  <div className={`p-2 rounded-lg mb-3 ${
                    recommendation.urgency === 'urgent' ? 'bg-destructive/10 text-destructive' :
                    recommendation.urgency === 'soon' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300' :
                    'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
                  }`}>
                    <p className="text-sm font-medium text-center">
                      💡 {recommendation.message}
                    </p>
                  </div>
                )}

                <div className="mb-3">
                  <Button 
                    variant={selectedMosqueId === mosque.id ? "default" : "outline"} 
                    size="sm" 
                    className="w-full"
                    onClick={() => handleMosqueSelect(mosque)}
                  >
                    <Bell className="h-4 w-4 mr-1" />
                    {selectedMosqueId === mosque.id ? "Selected for Notifications" : "Select for Notifications"}
                  </Button>
                </div>

                <div className="flex gap-2">
                  {mosque.phone && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => window.open(`tel:${mosque.phone}`, '_self')}
                    >
                      <Phone className="h-4 w-4 mr-1" />
                      Call
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${mosque.latitude},${mosque.longitude}`, '_blank')}
                  >
                    <MapPin className="h-4 w-4 mr-1" />
                    Directions
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default NearbyMosques;